import { useCallback, useEffect, useRef, useState } from "react";
import { trackProductEvent } from "../../analytics/trackEvent";
import { fetchFishingToday } from "./fishingTodayApi";
import {
  createFavoriteSpotFishingLocation,
  createLiveFishingLocation,
  createProfileFishingLocation,
  FISHING_TODAY_LOCATION_SOURCE,
  isCoordinateFishingLocation,
} from "./fishingTodayLocation";

const GPS_OPTIONS = Object.freeze({
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
});

const PRODUCT_EVENT_CONTEXT = Object.freeze({
  featureArea: "fishing_today",
});

const gpsFailureReason = (error) => {
  switch (error?.code) {
    case 1:
      return "permission_denied";
    case 2:
      return "position_unavailable";
    case 3:
      return "timeout";
    default:
      return "unknown";
  }
};

const emitEvent = (eventName, metadata) => {
  trackProductEvent(eventName, {
    ...PRODUCT_EVENT_CONTEXT,
    metadata,
  });
};

const locationRequestKey = (location) =>
  isCoordinateFishingLocation(location)
    ? `${location.source}:${location.latitude}:${location.longitude}:${
        location.identityKey || ""
      }`
    : FISHING_TODAY_LOCATION_SOURCE.PROFILE;

export function useFishingToday({
  favoriteSpotOutlookContext = null,
  onLeaveFavoriteSpot,
} = {}) {
  const initialLocationRef = useRef(null);
  if (initialLocationRef.current === null) {
    try {
      initialLocationRef.current = favoriteSpotOutlookContext
        ? createFavoriteSpotFishingLocation(favoriteSpotOutlookContext)
        : createProfileFishingLocation();
    } catch (_error) {
      initialLocationRef.current = createProfileFishingLocation();
    }
  }
  const favoriteSpotIdentityRef = useRef(
    initialLocationRef.current.source ===
      FISHING_TODAY_LOCATION_SOURCE.FAVORITE_SPOT
      ? initialLocationRef.current.identityKey
      : ""
  );
  const [state, setState] = useState({
    data: null,
    error: null,
    gpsError: null,
    isInitialLoading: true,
    isRefreshing: false,
    activeLocation: initialLocationRef.current,
  });
  const activeLocationRef = useRef(initialLocationRef.current);
  const lastSuccessfulLocationRef = useRef(initialLocationRef.current);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const pendingRequestRef = useRef(null);
  const mountedRef = useRef(false);
  const cleanupTimerRef = useRef(null);
  const initialLoadStartedRef = useRef(false);
  const gpsAttemptRef = useRef(0);
  const gpsAcquisitionPendingRef = useRef(false);
  const impressionSentRef = useRef(false);
  const lastLocationSourceEventRef = useRef(null);
  const marineShownSourcesRef = useRef(new Set());

  const load = useCallback(async (location, { initial = false } = {}) => {
    const requestKey = locationRequestKey(location);
    if (pendingRequestRef.current?.key === requestKey) return;

    const fallbackLocation = lastSuccessfulLocationRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestIdRef.current;
    pendingRequestRef.current = { id: requestId, key: requestKey };
    activeLocationRef.current = location;

    if (mountedRef.current) {
      setState((previous) => ({
        ...previous,
        error: null,
        isInitialLoading: initial && !previous.data,
        isRefreshing: !initial || Boolean(previous.data),
        activeLocation: location,
      }));
    }

    try {
      const data = await fetchFishingToday({
        location: isCoordinateFishingLocation(location) ? location : undefined,
        signal: controller.signal,
      });

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      lastSuccessfulLocationRef.current = location;

      setState((previous) => ({
        ...previous,
        data,
        error: null,
        gpsError: null,
        isInitialLoading: false,
        isRefreshing: false,
        activeLocation: location,
      }));

      if (!impressionSentRef.current) {
        impressionSentRef.current = true;
        emitEvent("fishing_today_impression");
      }
      if (lastLocationSourceEventRef.current !== location.source) {
        lastLocationSourceEventRef.current = location.source;
        emitEvent("fishing_today_location_source", { source: location.source });
      }
      if (
        data?.marine?.eligible &&
        data?.marine?.available &&
        !marineShownSourcesRef.current.has(location.source)
      ) {
        marineShownSourcesRef.current.add(location.source);
        emitEvent("fishing_today_marine_shown");
      }
    } catch (error) {
      if (
        error?.name === "AbortError" ||
        !mountedRef.current ||
        requestId !== requestIdRef.current
      ) {
        return;
      }

      setState((previous) => ({
        ...previous,
        error,
        isInitialLoading: false,
        isRefreshing: false,
        activeLocation: fallbackLocation,
      }));
      activeLocationRef.current = fallbackLocation;
    } finally {
      if (pendingRequestRef.current?.id === requestId) {
        pendingRequestRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    mountedRef.current = true;
    if (!initialLoadStartedRef.current) {
      initialLoadStartedRef.current = true;
      load(initialLocationRef.current, { initial: true });
    }
    return () => {
      mountedRef.current = false;
      cleanupTimerRef.current = setTimeout(() => {
        if (mountedRef.current) return;
        requestIdRef.current += 1;
        gpsAttemptRef.current += 1;
        gpsAcquisitionPendingRef.current = false;
        pendingRequestRef.current = null;
        controllerRef.current?.abort();
      }, 0);
    };
  }, [load]);

  useEffect(() => {
    const nextIdentity = favoriteSpotOutlookContext?.spotKey || "";
    if (favoriteSpotIdentityRef.current === nextIdentity) return;
    favoriteSpotIdentityRef.current = nextIdentity;

    if (!nextIdentity) {
      if (
        activeLocationRef.current.source ===
        FISHING_TODAY_LOCATION_SOURCE.FAVORITE_SPOT
      ) {
        load(createProfileFishingLocation());
      }
      return;
    }

    try {
      load(createFavoriteSpotFishingLocation(favoriteSpotOutlookContext));
    } catch (_error) {
      load(createProfileFishingLocation());
    }
  }, [favoriteSpotOutlookContext, load]);

  useEffect(() => {
    const handleProfileUpdate = () => load(activeLocationRef.current);
    window.addEventListener("userUpdated", handleProfileUpdate);
    return () => window.removeEventListener("userUpdated", handleProfileUpdate);
  }, [load]);

  const requestPreciseLocation = useCallback(() => {
    if (gpsAcquisitionPendingRef.current) return;
    emitEvent("fishing_today_precise_location_clicked");

    if (!navigator.geolocation?.getCurrentPosition) {
      emitEvent("fishing_today_gps_failure", { reason: "unsupported" });
      setState((previous) => ({ ...previous, gpsError: "unsupported" }));
      return;
    }

    const attemptId = ++gpsAttemptRef.current;
    gpsAcquisitionPendingRef.current = true;
    setState((previous) => ({ ...previous, gpsError: null, isRefreshing: true }));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!mountedRef.current || attemptId !== gpsAttemptRef.current) return;
        gpsAcquisitionPendingRef.current = false;
        let liveLocation;
        try {
          liveLocation = createLiveFishingLocation(position);
        } catch (_error) {
          emitEvent("fishing_today_gps_failure", { reason: "invalid_position" });
          setState((previous) => ({
            ...previous,
            gpsError: "position_unavailable",
            isRefreshing: false,
          }));
          return;
        }

        favoriteSpotIdentityRef.current = "";
        onLeaveFavoriteSpot?.();
        emitEvent("fishing_today_gps_success");
        load(liveLocation);
      },
      (error) => {
        if (!mountedRef.current || attemptId !== gpsAttemptRef.current) return;
        gpsAcquisitionPendingRef.current = false;
        const reason = gpsFailureReason(error);
        emitEvent("fishing_today_gps_failure", { reason });
        setState((previous) => ({
          ...previous,
          gpsError: reason,
          isRefreshing: false,
        }));
      },
      GPS_OPTIONS
    );
  }, [load, onLeaveFavoriteSpot]);

  const useProfileLocation = useCallback(() => {
    favoriteSpotIdentityRef.current = "";
    onLeaveFavoriteSpot?.();
    gpsAttemptRef.current += 1;
    gpsAcquisitionPendingRef.current = false;
    setState((previous) => ({ ...previous, gpsError: null }));
    load(createProfileFishingLocation());
  }, [load, onLeaveFavoriteSpot]);

  const retry = useCallback(() => {
    load(activeLocationRef.current);
  }, [load]);

  return {
    ...state,
    geolocationSupported: Boolean(navigator.geolocation?.getCurrentPosition),
    requestPreciseLocation,
    useProfileLocation,
    retry,
  };
}
